import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaAppleAlt,
  FaFileMedical,
  FaCalendarAlt,
  FaUtensils,
  FaBan,
  FaUserMd,
  FaBook,
  FaRunning,
  FaStickyNote,
  FaCheckCircle,
  FaTimes,
  FaArrowLeft,
  FaSave,
  FaRedo,
  FaTimesCircle,
  FaInfoCircle,
  FaShieldAlt,
  FaSun,
  FaMoon,
  FaCoffee,
  FaHamburger,
  FaPlus,
  FaMinus,
  FaUsers
} from 'react-icons/fa';
import { GiForkKnifeSpoon } from 'react-icons/gi';

// Firebase imports
import { database } from '../firebase'; 
import { ref as dbRef, push, runTransaction, set, get } from 'firebase/database';

const AdminAddDietPlan = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const isEditMode = Boolean(planId);

  const [formData, setFormData] = useState({
    diseaseName: '',
    duration: '',
    dietType: 'Vegetarian',
    doctorName: 'Dr. Niten Gathge (MD)',
    ageGroup: '',
    description: '',
    createdBy: 'Admin User'
  });

  const [foodsAllowed, setFoodsAllowed] = useState(['']);
  const [foodsToAvoid, setFoodsToAvoid] = useState(['']);
  const [breakfastItems, setBreakfastItems] = useState(['']);
  const [lunchItems, setLunchItems] = useState(['']);
  const [snacksItems, setSnacksItems] = useState(['']);
  const [dinnerItems, setDinnerItems] = useState(['']);
  const [notes, setNotes] = useState(['']);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(isEditMode);

  const dietTypes = ['Vegetarian', 'Non-Vegetarian', 'Both'];

  // Fetch existing diet plan data when in edit mode
  useEffect(() => {
    if (isEditMode && planId) {
      const fetchDietPlan = async () => {
        try {
          setLoading(true);
          const dietPlanRef = dbRef(database, `Dr/dietPlans/${planId}`);
          const snapshot = await get(dietPlanRef);
          
          if (snapshot.exists()) {
            const data = snapshot.val();
            
            // Populate form data
            setFormData({
              diseaseName: data.diseaseName || '',
              duration: data.duration?.toString() || '',
              dietType: data.dietType || 'Vegetarian',
              doctorName: data.doctorName || 'Dr. Niten Gathge (MD)',
              ageGroup: data.ageGroup || '',
              description: data.description || '',
              createdBy: data.createdBy || 'Admin User'
            });
            
            // Populate arrays
            setFoodsAllowed(data.foodsAllowed?.length > 0 ? data.foodsAllowed : ['']);
            setFoodsToAvoid(data.foodsToAvoid?.length > 0 ? data.foodsToAvoid : ['']);
            setBreakfastItems(data.breakfast?.length > 0 ? data.breakfast : ['']);
            setLunchItems(data.lunch?.length > 0 ? data.lunch : ['']);
            setSnacksItems(data.snacks?.length > 0 ? data.snacks : ['']);
            setDinnerItems(data.dinner?.length > 0 ? data.dinner : ['']);
            setNotes(data.notes?.length > 0 ? data.notes : ['']);
          } else {
            setErrors({ submit: 'Diet plan not found' });
            setTimeout(() => navigate('/admin/diet-plans'), 2000);
          }
        } catch (error) {
          console.error('Error fetching diet plan:', error);
          setErrors({ submit: 'Failed to load diet plan data' });
        } finally {
          setLoading(false);
        }
      };
      
      fetchDietPlan();
    }
  }, [isEditMode, planId, navigate]);

  const commonDiseases = [
    'Diabetes', 'Blood Pressure (Hypertension)', 'Heart Disease', 'Obesity / Weight Loss',
    'Thyroid', 'Cholesterol', 'Kidney Disease', 'Liver Disease', 'PCOD/PCOS',
    'Gastrointestinal Issues', 'Arthritis', 'Allergies', 'Cancer Recovery', 'General Wellness'
  ];

  // Helper functions for dynamic inputs
  const addInputField = (array, setArray) => setArray([...array, '']);
  const removeInputField = (array, setArray, index) => {
    if (array.length > 1) setArray(array.filter((_, i) => i !== index));
  };
  const updateInputField = (array, setArray, index, value) => {
    const newArray = [...array];
    newArray[index] = value;
    setArray(newArray);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const selectDisease = (disease) => {
    setFormData(prev => ({ ...prev, diseaseName: disease }));
    if (errors.diseaseName) setErrors(prev => ({ ...prev, diseaseName: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.diseaseName.trim()) newErrors.diseaseName = 'Disease name is required';
    if (!formData.duration || parseInt(formData.duration) <= 0) newErrors.duration = 'Valid duration is required';
    if (!formData.ageGroup.trim()) newErrors.ageGroup = 'Age group is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';

    if (!foodsAllowed.some(item => item.trim())) newErrors.foodsAllowed = 'At least one allowed food is required';
    if (!foodsToAvoid.some(item => item.trim())) newErrors.foodsToAvoid = 'At least one food to avoid is required';
    if (!breakfastItems.some(item => item.trim())) newErrors.breakfast = 'Breakfast items are required';
    if (!lunchItems.some(item => item.trim())) newErrors.lunch = 'Lunch items are required';
    if (!dinnerItems.some(item => item.trim())) newErrors.dinner = 'Dinner items are required';

    return newErrors;
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  const validationErrors = validateForm();
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }

  setIsSubmitting(true);

  try {
    if (isEditMode) {
      /* ----------------------------------
         UPDATE MODE: Update existing diet plan
      -----------------------------------*/
      // Get existing data to preserve planId and planCode
      const existingPlanRef = dbRef(database, `Dr/dietPlans/${planId}`);
      const existingSnapshot = await get(existingPlanRef);
      const existingData = existingSnapshot.val();

      const dietPlanData = {
        planId: existingData.planId,
        planCode: existingData.planCode,
        diseaseName: formData.diseaseName.trim(),
        duration: parseInt(formData.duration),
        dietType: formData.dietType,
        doctorName: formData.doctorName.trim(),
        ageGroup: formData.ageGroup.trim(),
        description: formData.description.trim(),
        createdBy: existingData.createdBy,
        createdAt: existingData.createdAt,
        updatedAt: new Date().toISOString(),
        isActive: existingData.isActive,

        foodsAllowed: foodsAllowed.filter(item => item.trim()),
        foodsToAvoid: foodsToAvoid.filter(item => item.trim()),
        breakfast: breakfastItems.filter(item => item.trim()),
        lunch: lunchItems.filter(item => item.trim()),
        snacks: snacksItems.filter(item => item.trim()),
        dinner: dinnerItems.filter(item => item.trim()),
        notes: notes.filter(item => item.trim())
      };

      const dietPlanRef = dbRef(database, `Dr/dietPlans/${planId}`);
      await set(dietPlanRef, dietPlanData);

      setSuccessMessage("Diet plan updated successfully!");
    } else {
      /* ----------------------------------
         CREATE MODE: Get numeric ID using transaction
      -----------------------------------*/
      const counterRef = dbRef(database, "Dr/dietPlansCounter");

      const transactionResult = await runTransaction(counterRef, (current) => {
        return (current || 0) + 1;
      });

      if (!transactionResult.committed) {
        throw new Error("Failed to generate diet plan ID");
      }

      const newPlanId = transactionResult.snapshot.val().toString();

      /* ----------------------------------
         Prepare diet plan data
      -----------------------------------*/
      const dietPlanData = {
        planId: newPlanId,
        diseaseName: formData.diseaseName.trim(),
        duration: parseInt(formData.duration),
        dietType: formData.dietType,
        doctorName: formData.doctorName.trim(),
        ageGroup: formData.ageGroup.trim(),
        description: formData.description.trim(),
        createdBy: formData.createdBy,
        createdAt: new Date().toISOString(),
        isActive: true,
        planCode: `DIET-${newPlanId.padStart(5, "0")}`,

        foodsAllowed: foodsAllowed.filter(item => item.trim()),
        foodsToAvoid: foodsToAvoid.filter(item => item.trim()),
        breakfast: breakfastItems.filter(item => item.trim()),
        lunch: lunchItems.filter(item => item.trim()),
        snacks: snacksItems.filter(item => item.trim()),
        dinner: dinnerItems.filter(item => item.trim()),
        notes: notes.filter(item => item.trim())
      };

      /* ----------------------------------
         Save using numeric ID
      -----------------------------------*/
      const dietPlanRef = dbRef(database, `Dr/dietPlans/${newPlanId}`);
      await set(dietPlanRef, dietPlanData);

      setSuccessMessage("Diet plan added successfully!");
    }

    setTimeout(() => {
      navigate("/admin/diet-plans");
    }, 2000);

  } catch (error) {
    console.error("Error saving diet plan:", error);
    setErrors({ submit: `Failed to ${isEditMode ? 'update' : 'save'} diet plan. Please try again.` });
  } finally {
    setIsSubmitting(false);
  }
};


  const handleReset = () => {
    setFormData({
      diseaseName: '',
      duration: '',
      dietType: 'Vegetarian',
      doctorName: 'Dr. Niten Gathge (MD)',
      ageGroup: '',
      description: '',
      createdBy: 'Admin User'
    });
    setFoodsAllowed(['']);
    setFoodsToAvoid(['']);
    setBreakfastItems(['']);
    setLunchItems(['']);
    setSnacksItems(['']);
    setDinnerItems(['']);
    setNotes(['']);
    setErrors({});
    setSuccessMessage('');
  };

  const handleCancel = () => navigate('/admin/diet-plans');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading diet plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button onClick={handleCancel} className="flex items-center text-teal-600 hover:text-teal-700 font-medium mb-4">
            <FaArrowLeft className="mr-2" /> Back to Diet Plans
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{isEditMode ? 'Edit Diet Plan' : 'Add New Diet Plan'}</h1>
          <p className="text-gray-600 mt-2">{isEditMode ? 'Update the diet plan details' : 'Create a structured diet plan for health conditions'}</p>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center">
            <FaCheckCircle className="text-green-600 text-xl mr-3" />
            <div>
              <p className="font-bold text-green-800">{successMessage}</p>
              <p className="text-sm text-green-600">Redirecting...</p>
            </div>
          </div>
        )}

        {errors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center">
            <FaTimesCircle className="text-red-600 text-xl mr-3" />
            <p className="font-bold text-red-800">{errors.submit}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                  <FaAppleAlt className="mr-3 text-emerald-600" /> Quick Setup
                </h2>

                {/* Common Diseases */}
                <div className="mb-6">
                  <h3 className="font-medium text-gray-700 mb-3">Common Conditions</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {commonDiseases.map((disease) => (
                      <button
                        key={disease}
                        type="button"
                        onClick={() => selectDisease(disease)}
                        className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                          formData.diseaseName === disease
                            ? 'bg-emerald-100 border-emerald-400 text-emerald-800 font-medium'
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {disease}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="p-5 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl">
                  <h4 className="font-semibold mb-3">Plan Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>Type:</span> <strong>{formData.dietType}</strong></div>
                    <div className="flex justify-between"><span>Duration:</span> <strong>{formData.duration || '-'} days</strong></div>
                    <div className="flex justify-between"><span>Age:</span> <strong>{formData.ageGroup || 'All'}</strong></div>
                    <div className="flex justify-between"><span>Doctor:</span> <strong>{formData.doctorName}</strong></div>
                  </div>
                </div>

                {/* Tips */}
                <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h4 className="font-medium flex items-center mb-2">
                    <FaInfoCircle className="mr-2 text-amber-600" /> Tips
                  </h4>
                  <ul className="text-xs text-gray-700 space-y-1">
                    <li>• Include portion sizes</li>
                    <li>• Add hydration tips</li>
                    <li>• Suggest alternatives</li>
                    <li>• Keep it sustainable</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Main Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Disease Name */}
                  <div className="md:col-span-2">
                    <label className="block font-medium text-gray-700 mb-2">Disease/Condition *</label>
                    <div className="relative">
                      <FaFileMedical className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="text" name="diseaseName" value={formData.diseaseName} onChange={handleChange}
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 ${errors.diseaseName ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="e.g., Diabetes Type 2"
                      />
                    </div>
                    {errors.diseaseName && <p className="text-red-600 text-sm mt-1">{errors.diseaseName}</p>}
                  </div>

                  {/* Duration & Diet Type */}
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Duration (Days) *</label>
                    <div className="relative">
                      <FaCalendarAlt className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="number" name="duration" value={formData.duration} onChange={handleChange}
                        min="1" max="365"
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 ${errors.duration ? 'border-red-300' : 'border-gray-300'}`}
                      />
                    </div>
                    {errors.duration && <p className="text-red-600 text-sm mt-1">{errors.duration}</p>}
                  </div>

                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Diet Type</label>
                    <select name="dietType" value={formData.dietType} onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500">
                      {dietTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  {/* Doctor & Age Group */}
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Doctor Name</label>
                    <div className="relative">
                      <FaUserMd className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="text" name="doctorName" value={formData.doctorName} onChange={handleChange}
                        className="pl-10 w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium text-gray-700 mb-2">Age Group *</label>
                    <div className="relative">
                      <FaUsers className="absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="text" name="ageGroup" value={formData.ageGroup} onChange={handleChange}
                        className={`pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 ${errors.ageGroup ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="e.g., Adults 30-60, All Ages"
                      />
                    </div>
                    {errors.ageGroup && <p className="text-red-600 text-sm mt-1">{errors.ageGroup}</p>}
                  </div>
                </div>

                {/* Foods Allowed */}
                <div className="mt-8">
                  <div className="flex justify-between items-center mb-4">
                    <label className="font-medium text-gray-700 flex items-center">
                      <FaUtensils className="mr-2 text-emerald-600" /> Foods Allowed *
                    </label>
                    <button type="button" onClick={() => addInputField(foodsAllowed, setFoodsAllowed)}
                      className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 flex items-center">
                      <FaPlus className="mr-1" /> Add
                    </button>
                  </div>
                  <div className="space-y-3">
                    {foodsAllowed.map((item, i) => (
                      <div key={i} className="flex gap-3">
                        <input
                          type="text" value={item}
                          onChange={(e) => updateInputField(foodsAllowed, setFoodsAllowed, i, e.target.value)}
                          className="flex-1 p-3 border rounded-xl focus:ring-2 focus:ring-teal-500"
                          placeholder="e.g., Whole grains, leafy greens"
                        />
                        {foodsAllowed.length > 1 && (
                          <button type="button" onClick={() => removeInputField(foodsAllowed, setFoodsAllowed, i)}
                            className="p-3 text-red-600 hover:bg-red-50 rounded-xl">
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {errors.foodsAllowed && <p className="text-red-600 text-sm mt-2">{errors.foodsAllowed}</p>}
                </div>

                {/* Foods to Avoid */}
                <div className="mt-8">
                  <div className="flex justify-between items-center mb-4">
                    <label className="font-medium text-gray-700 flex items-center">
                      <FaBan className="mr-2 text-red-600" /> Foods to Avoid *
                    </label>
                    <button type="button" onClick={() => addInputField(foodsToAvoid, setFoodsToAvoid)}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center">
                      <FaPlus className="mr-1" /> Add
                    </button>
                  </div>
                  <div className="space-y-3">
                    {foodsToAvoid.map((item, i) => (
                      <div key={i} className="flex gap-3">
                        <input
                          type="text" value={item}
                          onChange={(e) => updateInputField(foodsToAvoid, setFoodsToAvoid, i, e.target.value)}
                          className="flex-1 p-3 border rounded-xl focus:ring-2 focus:ring-teal-500"
                          placeholder="e.g., Processed sugar, fried foods"
                        />
                        {foodsToAvoid.length > 1 && (
                          <button type="button" onClick={() => removeInputField(foodsToAvoid, setFoodsToAvoid, i)}
                            className="p-3 text-red-600 hover:bg-red-50 rounded-xl">
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {errors.foodsToAvoid && <p className="text-red-600 text-sm mt-2">{errors.foodsToAvoid}</p>}
                </div>

                {/* Meal Plan */}
                <div className="mt-10 pt-6 border-t border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                    <GiForkKnifeSpoon className="mr-3 text-teal-600" /> Daily Meal Plan
                  </h3>

                  <div className="grid md:grid-cols-2 gap-8">
                    {[
                      { label: 'Breakfast', icon: FaSun, color: 'amber', items: breakfastItems, setItems: setBreakfastItems },
                      { label: 'Lunch', icon: FaHamburger, color: 'orange', items: lunchItems, setItems: setLunchItems },
                      { label: 'Snacks', icon: FaCoffee, color: 'gray', items: snacksItems, setItems: setSnacksItems },
                      { label: 'Dinner', icon: FaMoon, color: 'blue', items: dinnerItems, setItems: setDinnerItems }
                    ].map(({ label, icon: Icon, color, items, setItems }) => (
                      <div key={label}>
                        <div className="flex justify-between mb-3">
                          <label className="font-medium flex items-center">
                            <Icon className={`mr-2 text-${color}-600`} /> {label} {label !== 'Snacks' && '*'}
                          </label>
                          <button type="button" onClick={() => addInputField(items, setItems)}
                            className={`px-3 py-1 bg-${color}-100 text-${color}-700 rounded-lg hover:bg-${color}-200 text-sm`}>
                            <FaPlus />
                          </button>
                        </div>
                        <div className="space-y-3">
                          {items.map((item, i) => (
                            <div key={i} className="flex gap-3">
                              <input
                                type="text" value={item}
                                onChange={(e) => updateInputField(items, setItems, i, e.target.value)}
                                className="flex-1 p-3 border rounded-xl focus:ring-2 focus:ring-teal-500"
                                placeholder={`e.g., Oats with fruits`}
                              />
                              {items.length > 1 && (
                                <button type="button" onClick={() => removeInputField(items, setItems, i)}
                                  className="p-3 text-red-600 hover:bg-red-50 rounded-xl">
                                  <FaTimes />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {errors[label.toLowerCase()] && <p className="text-red-600 text-sm mt-2">{errors[label.toLowerCase()]}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Description & Notes */}
                <div className="mt-8">
                  <label className="block font-medium text-gray-700 mb-2">Description *</label>
                  <textarea
                    name="description" value={formData.description} onChange={handleChange}
                    rows="4"
                    className={`w-full p-4 border rounded-xl focus:ring-2 focus:ring-teal-500 ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="Explain the purpose, benefits, and guidelines of this diet plan..."
                  />
                  {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
                </div>

                <div className="mt-8">
                  <div className="flex justify-between mb-3">
                    <label className="font-medium">Additional Notes (Optional)</label>
                    <button type="button" onClick={() => addInputField(notes, setNotes)}
                      className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200">
                      <FaPlus className="mr-1" /> Add Note
                    </button>
                  </div>
                  <div className="space-y-3">
                    {notes.map((note, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="relative flex-1">
                          <FaStickyNote className="absolute left-3 top-3.5 text-gray-400" />
                          <input
                            type="text" value={note}
                            onChange={(e) => updateInputField(notes, setNotes, i, e.target.value)}
                            className="pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500"
                            placeholder="e.g., Drink 8 glasses of water daily"
                          />
                        </div>
                        {notes.length > 1 && (
                          <button type="button" onClick={() => removeInputField(notes, setNotes, i)}
                            className="p-3 text-red-600 hover:bg-red-50 rounded-xl">
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Buttons */}
                <div className="mt-10 pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex gap-3">
                    <button type="button" onClick={handleCancel}
                      className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center">
                      <FaTimes className="mr-2" /> Cancel
                    </button>
                    <button type="button" onClick={handleReset}
                      className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center">
                      <FaRedo className="mr-2" /> Reset
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-8 py-3 rounded-xl font-bold text-white flex items-center justify-center ${
                      isSubmitting
                        ? 'bg-teal-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:shadow-lg'
                    }`}
                  >
                    {isSubmitting ? (
                      <>{isEditMode ? 'Updating' : 'Saving'} Diet Plan...</>
                    ) : (
                      <><FaSave className="mr-2" /> {isEditMode ? 'Update' : 'Save'} Diet Plan</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddDietPlan;